/**
 * =============================================================================
 * IDS CRM Web - Engineer Detail Page
 * =============================================================================
 *
 * PURPOSE:
 * Engineer profile with schedule, time entries, expenses, and weekly summary.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { API_URL, useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import AuditLogPanel from '../components/AuditLogPanel';
import { ENGINEER_AUDIT_FIELD_LABELS } from '../utils/auditFieldLabels';
import { roleLabel } from '../utils/permissions';
import { ENGINEER_STATUS_ROLES } from '../utils/geolocation';
import { formatDateKey } from '../utils/dates';
import BackToList from '../components/BackToList';
import './EngineerDetail.css';

// Two doors, one component (Gareth 2026-08-10): the /engineers/:id route
// (managers opening team members' records) and the Settings → My Profile tab
// (everyone's own record, `userId` prop + embedded). Embedded mode leaves the
// URL query string alone — it belongs to the Settings page's ?tab= state.
function EngineerDetail({ userId: userIdProp, embedded = false }) {
  const { id: routeId } = useParams();
  const id = userIdProp || routeId;
  const [searchParams, setSearchParams] = useSearchParams();
  const { authFetch, user } = useAuth();
  const { toastElement, showToast } = useToast();
  const { confirmElement, requestConfirm } = useConfirm();

  const [engineer, setEngineer] = useState(null);
  const [schedule, setSchedule] = useState({ tasks: [], open_tickets: [], on_call: [] });
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(!embedded && searchParams.get('edit') === '1' ? 'profile' : 'schedule');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editMode, setEditMode] = useState(!embedded && searchParams.get('edit') === '1');
  const [profileForm, setProfileForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [message, setMessage] = useState(null);
  const photoInputRef = useRef(null);

  // Gareth 2026-08-03/10: everyone edits their OWN profile basics (contact
  // details, home base, notification prefs — the server's SELF_EDIT_FIELDS);
  // management edit everything. This is the ONLY surface for those fields.
  const isSelf = user?.userId === id;
  const isSelfOnlyEditor = isSelf && !['admin', 'manager'].includes(user?.role);
  const canEdit = ['admin', 'manager'].includes(user?.role) || isSelf;
  const canEditAdminFields = user?.role === 'admin';

  // On/off-duty status belongs to the roles that clock in and out (they're
  // the overtime-eligible ones, Gareth 2026-08-10) — same list that gets the
  // sidebar clock widget. Office roles show no duty state or location.
  const hasDutyStatus = ENGINEER_STATUS_ROLES.includes(engineer?.role);

  const fetchEngineer = useCallback(async () => {
    try {
      const [engRes, schedRes, sumRes] = await Promise.all([
        authFetch(`/api/v1/engineers/${id}`),
        authFetch(`/api/v1/engineers/${id}/schedule`),
        authFetch(`/api/v1/engineers/${id}/summary`)
      ]);

      if (!engRes.ok) throw new Error('Engineer not found');

      const engData = await engRes.json();
      const schedData = await schedRes.json();
      const sumData = await sumRes.json();

      setEngineer(engData);
      setSchedule({
        tasks: schedData.tasks || [],
        open_tickets: schedData.open_tickets || [],
        on_call: schedData.on_call || []
      });
      setSummary(sumData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => {
    fetchEngineer();
  }, [fetchEngineer]);

  useEffect(() => {
    if (!engineer) return;
    setProfileForm({
      full_name: engineer.full_name || '',
      preferred_name: engineer.preferred_name || '',
      email: engineer.email || '',
      employee_number: engineer.employee_number || '',
      mobile: engineer.mobile || '',
      personal_email: engineer.personal_email || '',
      home_address: engineer.home_address || '',
      home_postcode: engineer.home_postcode || '',
      home_lat: engineer.home_lat ?? '',
      home_lng: engineer.home_lng ?? '',
      is_active: Boolean(engineer.is_active),
      engineer_initials: engineer.engineer_initials || getInitials(engineer.full_name).slice(0, 4),
      primary_region: engineer.primary_region || '',
      can_cover_regions: (engineer.can_cover_regions || []).join(', '),
      group_memberships: (engineer.group_memberships || []).join(', '),
      can_assign_work: engineer.can_assign_work !== false,
      contract_type: engineer.contract_type || 'full_time',
      start_date: engineer.start_date ? String(engineer.start_date).slice(0, 10) : ''
    });
  }, [engineer]);

  const getStatusClass = (status) => {
    switch (status) {
      case 'available': return 'status-available';
      case 'on_job': return 'status-on-job';
      case 'on_site': return 'status-on-job';
      case 'travelling': return 'status-travelling';
      case 'travelling_home': return 'status-travelling';
      case 'on_call': return 'status-on-call';
      case 'off_duty': return 'status-off-duty';
      default: return '';
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const formatHours = (hours) => {
    const h = parseFloat(hours) || 0;
    return h.toFixed(1);
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showToast('Image must be less than 2MB', 'error');
      return;
    }

    setUploadingPhoto(true);
    setMessage(null);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const response = await authFetch(`/api/v1/engineers/${id}/photo`, {
          method: 'POST',
          body: JSON.stringify({ photo: reader.result })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to upload photo');
        setEngineer(prev => ({ ...prev, photo_url: data.photo_url }));
        setMessage('Engineer photo updated');
      } catch (err) {
        showToast(err.message || 'Failed to upload photo', 'error');
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const removePhoto = (event) => {
    event.stopPropagation();
    requestConfirm({
      title: 'Remove photo?',
      message: 'Remove engineer photo?',
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        const response = await authFetch(`/api/v1/engineers/${id}/photo`, { method: 'DELETE' });
        if (response.ok) {
          setEngineer(prev => ({ ...prev, photo_url: null }));
          setMessage('Engineer photo removed');
        }
      }
    });
  };

  const updateProfileField = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  // Home-base coordinate lookup (came home from the short-lived My Account
  // tab, 2026-08-10) — postcode → lat/lng via the geocoding proxy.
  const [homeGeocoding, setHomeGeocoding] = useState(false);
  const [homeGeocodeMessage, setHomeGeocodeMessage] = useState(null);
  const findHomeCoordinates = async () => {
    setHomeGeocoding(true);
    setHomeGeocodeMessage(null);
    try {
      const response = await authFetch('/api/v1/geocode-address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode: profileForm.home_postcode.trim(), country_code: 'GB' })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setProfileForm(prev => ({ ...prev, home_lat: String(data.latitude), home_lng: String(data.longitude) }));
        setHomeGeocodeMessage({ type: 'ok', text: `Matched: ${data.displayName || 'postcode area'}. Save to store.` });
      } else {
        setHomeGeocodeMessage({ type: 'error', text: data.error || 'No match found' });
      }
    } catch {
      setHomeGeocodeMessage({ type: 'error', text: 'Lookup failed — try again' });
    } finally {
      setHomeGeocoding(false);
    }
  };

  const parseCsv = (value) => String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

  const openEditMode = () => {
    setEditMode(true);
    if (!embedded) setSearchParams({ edit: '1' });
    setActiveTab('profile');
  };

  const closeEditMode = () => {
    setEditMode(false);
    if (!embedded) setSearchParams({});
  };

  const saveProfile = async () => {
    if (!profileForm) return;
    setSavingProfile(true);
    setMessage(null);
    try {
      // Both branches carry the personal basics (the server's
      // SELF_EDIT_FIELDS); management additionally send the operational set.
      const personalFields = {
        preferred_name: profileForm.preferred_name || null,
        mobile: profileForm.mobile || null,
        personal_email: profileForm.personal_email || null,
        home_address: profileForm.home_address || null,
        home_postcode: profileForm.home_postcode || null,
        home_lat: profileForm.home_lat === '' ? null : Number(profileForm.home_lat),
        home_lng: profileForm.home_lng === '' ? null : Number(profileForm.home_lng)
      };
      const payload = isSelfOnlyEditor ? personalFields : {
        ...personalFields,
        full_name: profileForm.full_name,
        engineer_initials: profileForm.engineer_initials || null,
        primary_region: profileForm.primary_region || null,
        can_cover_regions: parseCsv(profileForm.can_cover_regions),
        group_memberships: parseCsv(profileForm.group_memberships),
        can_assign_work: profileForm.can_assign_work,
        contract_type: profileForm.contract_type,
        start_date: profileForm.start_date || null
      };

      if (canEditAdminFields) {
        payload.email = profileForm.email;
        payload.employee_number = profileForm.employee_number || null;
        payload.is_active = profileForm.is_active;
      }

      const response = await authFetch(`/api/v1/engineers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save profile');
      setEngineer(prev => ({ ...prev, ...data }));
      setMessage('Engineer profile saved');
      closeEditMode();
    } catch (err) {
      showToast(err.message || 'Failed to save profile', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return <div className="loading-page">Loading engineer...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Error</h2>
        <p>{error}</p>
        <BackToList to="/engineers">← Engineers</BackToList>
      </div>
    );
  }

  return (
    <div className="engineer-detail-page">
      {toastElement}
      {confirmElement}
      <input
        type="file"
        ref={photoInputRef}
        onChange={uploadPhoto}
        accept="image/*"
        style={{ display: 'none' }}
      />
      {/* Header */}
      <div className="engineer-header">
        {!embedded && <BackToList to="/engineers" className="back-link">&larr; Engineers</BackToList>}

        <div className="header-main">
          <div
            className={`avatar-large ${hasDutyStatus ? getStatusClass(engineer.current_status) : ''} ${uploadingPhoto ? 'uploading' : ''}`}
            onClick={canEdit ? () => photoInputRef.current?.click() : undefined}
            title={canEdit ? 'Click to upload engineer photo' : undefined}
          >
            {engineer.photo_url ? (
              <>
                <img src={`${API_URL}${engineer.photo_url}`} alt={engineer.full_name} />
                {canEdit && (
                  <button type="button" className="remove-photo-btn" onClick={removePhoto} title="Remove photo">
                    ×
                  </button>
                )}
              </>
            ) : (
              <>
                <span>{getInitials(engineer.full_name)}</span>
                {canEdit && <span className="photo-upload-hint">+</span>}
              </>
            )}
            {uploadingPhoto && <span className="photo-spinner"></span>}
          </div>

          <div className="header-info">
            <h1>{engineer.full_name}</h1>
            <div className="header-meta">
              <span className="employee-number">{engineer.employee_number || 'No ID'}</span>
              {/* On/off-duty is an ENGINEER fact (clocking, overtime,
                  tracking) — office roles have no duty state to show. */}
              {hasDutyStatus && (
                <span className={`status-badge ${getStatusClass(engineer.current_status)}`}>
                  {engineer.current_status?.replace(/_/g, ' ')}
                </span>
              )}
              <span className="region">{engineer.primary_region || 'No region'}</span>
            </div>
          </div>

          {canEdit && (
            <div className="profile-header-actions">
              {editMode ? (
                <>
                  <button type="button" className="btn-secondary" onClick={closeEditMode}>Cancel</button>
                  <button type="button" className="btn-primary" onClick={saveProfile} disabled={savingProfile}>
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button type="button" className="btn-primary" onClick={openEditMode}>Edit Profile</button>
              )}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-value">{engineer.stats?.tasks_completed_30d || 0}</div>
            <div className="stat-label">Tasks (30d)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{engineer.stats?.tickets_closed_30d || 0}</div>
            <div className="stat-label">Tickets (30d)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatHours(engineer.stats?.hours_this_week / 60)}</div>
            <div className="stat-label">Hours (Week)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{engineer.stats?.on_call_days_30d || 0}</div>
            <div className="stat-label">On-Call Days (30d)</div>
          </div>
        </div>
      </div>

      {message && <div className="engineer-info-alert">{message}</div>}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === 'schedule' ? 'active' : ''}
          onClick={() => setActiveTab('schedule')}
        >
          Schedule
        </button>
        <button
          className={activeTab === 'profile' ? 'active' : ''}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={activeTab === 'summary' ? 'active' : ''}
          onClick={() => setActiveTab('summary')}
        >
          Weekly Summary
        </button>
        {canEdit && (
          <button
            className={activeTab === 'history' ? 'active' : ''}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'schedule' && (
          <div className="schedule-tab">
            {/* Upcoming Tasks */}
            <div className="section-card">
              <div className="card-header">
                <h3>Upcoming Tasks</h3>
                <span className="count">{schedule.tasks.length}</span>
              </div>
              <div className="card-body">
                {schedule.tasks.length === 0 ? (
                  <div className="empty">No scheduled tasks</div>
                ) : (
                  <div className="task-list">
                    {schedule.tasks.map(task => (
                      <Link key={task.task_id} to={`/tasks/${task.task_id}`} className="task-item">
                        <div className="task-date">
                          {formatDateKey(task.scheduled_date, { weekday: 'short', day: 'numeric', month: 'short' })}
                          {task.scheduled_start_time && ` ${task.scheduled_start_time.substring(0, 5)}`}
                        </div>
                        <div className="task-info">
                          <div className="task-type">{task.task_type?.replace(/_/g, ' ')}</div>
                          <div className="task-customer">{task.customer_name}</div>
                        </div>
                        <span className={`task-status status-${task.status}`}>{task.status}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Open Tickets */}
            <div className="section-card">
              <div className="card-header">
                <h3>Open Tickets</h3>
                <span className="count">{schedule.open_tickets.length}</span>
              </div>
              <div className="card-body">
                {schedule.open_tickets.length === 0 ? (
                  <div className="empty">No open tickets</div>
                ) : (
                  <div className="ticket-list">
                    {schedule.open_tickets.map(ticket => (
                      <Link key={ticket.ticket_id} to={`/tickets/${ticket.ticket_id}`} className="ticket-item">
                        <span className={`priority-dot priority-${ticket.priority}`}></span>
                        <div className="ticket-info">
                          <div className="ticket-ref">{ticket.ticket_ref}</div>
                          <div className="ticket-subject">{ticket.subject}</div>
                        </div>
                        <span className="ticket-customer">{ticket.customer_name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* On-Call */}
            {schedule.on_call.length > 0 && (
              <div className="section-card">
                <div className="card-header">
                  <h3>On-Call Shifts</h3>
                </div>
                <div className="card-body">
                  {schedule.on_call.map(oc => (
                    <div key={oc.assignment_id} className="on-call-item">
                      <div className="on-call-week">
                        {formatDateKey(oc.rota_date, { weekday: 'short', day: 'numeric', month: 'short' })}
                        {oc.cover_type_label ? ` · ${oc.cover_type_label}` : ''}
                      </div>
                      {oc.notes && (
                        <div className="on-call-backup">{oc.notes}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="profile-tab">
            <div className="profile-grid">
              <div className="section-card">
                <div className="card-header"><h3>Basic details</h3></div>
                <div className="card-body">
                  {editMode && profileForm ? (
                    <div className="profile-edit-grid">
                      <label>Full name<input value={profileForm.full_name} onChange={e => updateProfileField('full_name', e.target.value)} disabled={isSelfOnlyEditor} title={isSelfOnlyEditor ? 'Managers change names' : undefined} /></label>
                      <label>Preferred name<input value={profileForm.preferred_name} onChange={e => updateProfileField('preferred_name', e.target.value)} /></label>
                      <label>Email<input value={profileForm.email} onChange={e => updateProfileField('email', e.target.value)} disabled={!canEditAdminFields} /></label>
                      <label>Employee number<input value={profileForm.employee_number} onChange={e => updateProfileField('employee_number', e.target.value)} disabled={!canEditAdminFields} /></label>
                      <label>Mobile<input value={profileForm.mobile} onChange={e => updateProfileField('mobile', e.target.value)} /></label>
                      <label>Personal email<input type="email" value={profileForm.personal_email} onChange={e => updateProfileField('personal_email', e.target.value)} /></label>
                    </div>
                  ) : (
                    <>
                      <div className="info-row"><label>Email</label><span>{engineer.email}</span></div>
                      <div className="info-row"><label>Mobile</label><span>{engineer.mobile || '-'}</span></div>
                      <div className="info-row"><label>Personal email</label><span>{engineer.personal_email || '-'}</span></div>
                    </>
                  )}
                </div>
              </div>

              <div className="section-card">
                <div className="card-header"><h3>Engineer settings</h3></div>
                <div className="card-body">
                  {editMode && profileForm ? (
                    <div className="profile-edit-grid">
                      <label>Initials<input maxLength="8" value={profileForm.engineer_initials} onChange={e => updateProfileField('engineer_initials', e.target.value.toUpperCase())} disabled={isSelfOnlyEditor} /></label>
                      <label>Role
                        {/* Read-only here by design: role/privilege changes must go through
                            Settings -> Users, the single authoritative path with its own
                            self-change guard - not this operational profile-edit page. */}
                        <input value={roleLabel(engineer.role)} disabled readOnly title="Change role from Settings > Users" />
                      </label>
                      <label>Contract
                        <select value={profileForm.contract_type} onChange={e => updateProfileField('contract_type', e.target.value)} disabled={isSelfOnlyEditor}>
                          <option value="full_time">Full time</option>
                          <option value="part_time">Part time</option>
                          <option value="contractor">Contractor</option>
                        </select>
                      </label>
                      <label>Start date<input type="date" value={profileForm.start_date} onChange={e => updateProfileField('start_date', e.target.value)} disabled={isSelfOnlyEditor} /></label>
                      <label className="profile-checkbox"><input type="checkbox" checked={profileForm.is_active} onChange={e => updateProfileField('is_active', e.target.checked)} disabled={!canEditAdminFields} /> Active account</label>
                    </div>
                  ) : (
                    <>
                      <div className="info-row"><label>Initials</label><span>{engineer.engineer_initials || getInitials(engineer.full_name)}</span></div>
                      <div className="info-row"><label>Role</label><span>{roleLabel(engineer.role)}</span></div>
                      <div className="info-row"><label>Status</label><span>{engineer.is_active ? 'Active' : 'Inactive'}</span></div>
                      <div className="info-row"><label>Contract</label><span>{engineer.contract_type?.replace(/_/g, ' ')}</span></div>
                      <div className="info-row"><label>Start date</label><span>{formatDateKey(engineer.start_date)}</span></div>
                    </>
                  )}
                </div>
              </div>

              <div className="section-card">
                <div className="card-header"><h3>Assignment / on-call settings</h3></div>
                <div className="card-body">
                  {editMode && profileForm ? (
                    <div className="profile-edit-grid">
                      <label>Team/region<input value={profileForm.primary_region} onChange={e => updateProfileField('primary_region', e.target.value)} disabled={isSelfOnlyEditor} /></label>
                      <label>Can cover regions<input value={profileForm.can_cover_regions} onChange={e => updateProfileField('can_cover_regions', e.target.value)} placeholder="Comma separated" disabled={isSelfOnlyEditor} /></label>
                      <label className="profile-checkbox"><input type="checkbox" checked={profileForm.can_assign_work} onChange={e => updateProfileField('can_assign_work', e.target.checked)} disabled={isSelfOnlyEditor} /> Can be assigned tickets/jobs</label>
                      {/* Rota inclusion changes ONLY via Settings → Users
                          (Gareth 2026-08-10) — read-only here, even mid-edit. */}
                      <div className="info-row profile-wide"><label>On-call eligible</label><span>{engineer.on_call_eligible === false ? 'No' : 'Yes'} — set in Settings → Users</span></div>
                    </div>
                  ) : (
                    <>
                      <div className="info-row"><label>Team/region</label><span>{engineer.primary_region || '-'}</span></div>
                      {hasDutyStatus && (
                      <div className="info-row">
                        <label>Last known location</label>
                        <span>
                          {engineer.last_location_at
                            ? (Date.now() - new Date(engineer.last_location_at).getTime() < 2 * 60 * 60 * 1000
                              ? `Live (${new Date(engineer.last_location_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })})`
                              : `Not updated since ${new Date(engineer.last_location_at).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}`)
                            : (['off_duty', 'annual_leave', 'sick'].includes(engineer.current_status)
                              ? 'Off duty — not tracked'
                              : 'Location unknown')}
                        </span>
                      </div>
                      )}
                      <div className="info-row"><label>Can cover</label><span>{engineer.can_cover_regions?.join(', ') || '-'}</span></div>
                      <div className="info-row"><label>Assignable</label><span>{engineer.can_assign_work === false ? 'No' : 'Yes'}</span></div>
                      <div className="info-row"><label>On-call eligible</label><span>{engineer.on_call_eligible === false ? 'No' : 'Yes'}</span></div>
                    </>
                  )}
                </div>
              </div>

              <div className="section-card">
                <div className="card-header"><h3>Groups and permissions</h3></div>
                <div className="card-body">
                  {editMode && profileForm ? (
                    <div className="profile-edit-grid">
                      <label className="profile-wide">Groups/team membership<input value={profileForm.group_memberships} onChange={e => updateProfileField('group_memberships', e.target.value)} placeholder="Comma separated" disabled={isSelfOnlyEditor} /></label>
                    </div>
                  ) : (
                    <div className="info-row"><label>Groups</label><span>{engineer.group_memberships?.join(', ') || '-'}</span></div>
                  )}
                </div>
              </div>

              <div className="section-card">
                <div className="card-header"><h3>Home base</h3></div>
                <div className="card-body">
                  {editMode && profileForm ? (
                    <div className="profile-edit-grid">
                      <label className="profile-wide">Home address<input value={profileForm.home_address} onChange={e => updateProfileField('home_address', e.target.value)} /></label>
                      <label>Postcode<input value={profileForm.home_postcode} onChange={e => updateProfileField('home_postcode', e.target.value)} /></label>
                      <div className="profile-home-coords">
                        <button type="button" className="btn-secondary" onClick={findHomeCoordinates} disabled={homeGeocoding || !profileForm.home_postcode.trim()}>
                          {homeGeocoding ? 'Finding…' : 'Find coordinates'}
                        </button>
                        <span>
                          {profileForm.home_lat && profileForm.home_lng
                            ? `${Number(profileForm.home_lat).toFixed(5)}, ${Number(profileForm.home_lng).toFixed(5)}`
                            : 'No coordinates yet'}
                        </span>
                      </div>
                      {homeGeocodeMessage && (
                        <div className={`profile-geocode-message ${homeGeocodeMessage.type}`}>{homeGeocodeMessage.text}</div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="info-row"><label>Address</label><span>{engineer.home_address || '-'}</span></div>
                      <div className="info-row"><label>Postcode</label><span>{engineer.home_postcode || '-'}</span></div>
                      <div className="info-row"><label>Coordinates</label><span>{engineer.home_lat != null && engineer.home_lng != null ? `${Number(engineer.home_lat).toFixed(5)}, ${Number(engineer.home_lng).toFixed(5)}` : 'Not set'}</span></div>
                    </>
                  )}
                </div>
              </div>

              {/* 2026-10-14: notifications are chosen on Settings ->
                  Notifications, kind by kind, for email and Teams. The two
                  ticks that were here were never read by anything. */}
              <div className="section-card">
                <div className="card-header"><h3>Notifications</h3></div>
                <div className="card-body">
                  {isSelf ? (
                    <p className="profile-notifications-note">
                      Choose which notifications reach you by email and by Teams in{' '}
                      <Link to="/admin?tab=notifications">Settings, Notifications</Link>.
                    </p>
                  ) : (
                    <p className="profile-notifications-note">
                      Each person chooses their own email and Teams notifications in Settings, Notifications.
                    </p>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}
        {activeTab === 'summary' && summary && (
          <div className="summary-tab">
            <div className="summary-header">
              <h3>Week of {formatDateKey(summary.week_start, { day: 'numeric', month: 'long', year: 'numeric' })}</h3>
            </div>

            <div className="summary-grid">
              <div className="summary-card">
                <div className="summary-label">Total Hours</div>
                <div className="summary-value">{formatHours(summary.total_hours)}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">In Hours</div>
                <div className="summary-value">{formatHours(summary.in_hours_total)}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Out of Hours</div>
                <div className="summary-value">{formatHours(summary.out_of_hours_total)}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Overtime</div>
                <div className="summary-value">{formatHours(summary.overtime_hours)}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">On-Call Callouts</div>
                <div className="summary-value">{formatHours(summary.on_call_callout_hours)}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Miles</div>
                <div className="summary-value">{summary.total_miles || 0}</div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'history' && canEdit && (
          <AuditLogPanel
            fetchUrl={`/api/v1/engineers/${id}/audit-log`}
            fieldLabels={ENGINEER_AUDIT_FIELD_LABELS}
            emptyMessage="No changes have been recorded for this engineer's profile."
          />
        )}
      </div>
    </div>
  );
}

export default EngineerDetail;
